<template>
  <div>
    <div class="p-4 font-medium text-xs text-left leading-snug max-h-96 overflow-auto bg-charcoal-100 dark:bg-dark-950">
      <p class="font-bold text-2xl -mt-0.5">
        {{ $t('components.Event.review.title') }}
      </p>
      <p class="text-base mt-0.5">
        {{ $t('components.Event.review.content', { name: getNameList(organizers.map(o => o.name)), and: $t('common.and'), others: $t('common.others') }) }}
      </p>
      
      <div class="bg-white dark:bg-dark-800 dark:text-dark-50 py-3 pl-3 pr-1 rounded-xl text-light-950 shadow mt-4 space-y-2">
        <div class="flex items-center space-x-1.5" v-for="user in organizers" :user="user">
          <UserPicture :user="user" size="sm+" is-link class="shrink-0" />

          <div class="text-base font-medium line-clamp-1 break-all shrink-0 grow">
            {{ user.name }}
          </div>
          <div class="pr-1">
            <ButtonBase size="xs" :color="formData.kudos.includes(user._id) ? 'white' : 'base'" @click="() => formData.kudos.includes(user._id) ? formData.kudos = formData.kudos.filter(k => k !== user._id) : formData.kudos = [ ...formData.kudos, user._id ]">
              <div class="flex items-center">
                <ImageEmoji name="Clapping+Hands" class="size-6 -translate-y-0.5" :class="[ formData.kudos.includes(user._id) ? 'opacity-50' : '' ]" />

                <p class="font-bold ml-1.5 mr-1.5" :class="[ formData.kudos.includes(user._id) ? 'opacity-50' : '' ]">Merci !</p>

                <div class="size-5 bg-tiger-200 text-tiger-950 absolute -bottom-3 -right-2.5 rounded-full pointer-events-none flex items-center justify-center" v-if="formData.kudos.includes(user._id)">
                  <IconBase name="cute/check" :width="12" />
                </div>
              </div>
            </ButtonBase>
          </div>
        </div>

        <InputTags
          class="!mt-3 pt-3 border-t border-charcoal-100 dark:border-dark-600"
          :colors="{ selected: 'slate', active: 'gray', disabled: 'weak' }"
          v-model="formData.tags"
          size="md"
          :options="Object.values(EVENT_REVIEW_TAGS).filter(t => !t.isNegative).map(t => ({ value: t.value, label: $t(`components.Event.review.tags.${t.value}`) }))"
        />
      </div>
    </div>

    <div class="bg-white border-t border-charcoal-200 dark:bg-dark-800 dark:border-dark-600 flex items-center space-x-2 p-2.5">
      <ButtonBase style-type="round" class="shrink-0" color="white" @click="() => emit('previous')">
        <IconBase name="cute/angle-right" class="rotate-180" :width="20" />
      </ButtonBase>

      <ButtonBase color="builders" is-full :is-loading="isLoading" :is-disabled="formData.kudos.length <= 0 && formData.tags.length <= 0" @click="onNext">
        {{ $t('actions.send') }}
      </ButtonBase>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Event } from '@/classes'
import EventStore from '@/store/events'
import UserStore from '@/store/user'
import UsersStore from '@/store/users'
import { EVENT_REVIEW_TAGS } from '@/static/events'
import { InAppReview } from '@capacitor-community/in-app-review';

const props = withDefaults(defineProps<{
  event: Event
}>(), {

})

const { event } = toRefs(props)

const eventStore = EventStore()
const usersStore = UsersStore()
const userStore = UserStore()
const { currentUser } = storeToRefs(userStore)

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'previous'): void
  (e: 'reset'): void
}>()

const reviewExisting = computed(() => event.value.reviews.find(r => r.owner === currentUser.value?._id))

const formData: Ref<{
  tags: (keyof typeof EVENT_REVIEW_TAGS)[]
  kudos: string[]
}> = ref({
  tags: reviewExisting.value?.tags ?? [],
  kudos: reviewExisting.value?.kudos ?? []
})

const organizers = computed(() => usersStore.getItems(event.value.organizers.map(a => a._id)))

const isLoading = ref(false)
const route = useRoute()

const onNext = async () => {
  isLoading.value = true

  const response = await eventStore.review(props.event._id, formData.value)

  if (Math.random() < 0.5) InAppReview.requestReview()

  useNuxtApp().$amplitude.logEvent(`Event_Review`, {
    source: route.fullPath
  })

  emit('next')

  isLoading.value = false
}

</script>